"use client";

import { useEffect, useRef } from "react";
import { useDict } from "@/components/i18n/DictProvider";
import { useDismissSettled } from "@/components/pages/DismissableAd";
import { PlacementFrame } from "./PlacementFrame";
import { imageAlt } from "@/lib/images/alt";
import { aspectRatioCss } from "@/lib/placements/slots";
import {
  meetsViewabilityRule,
  VIEWABILITY_THRESHOLDS,
  VIEWABLE_MS,
} from "@/lib/placements/viewability";
import type { PlacementDecision } from "@/lib/placements/decision";

/**
 * One filled sponsor-banner slot: the creative inside a link to the tracking
 * redirect, in the shared editorial frame (PlacementFrame: label line, close,
 * aspect-ratio box reserved before paint), plus the two beacons the sponsor is
 * billed on. Renders nothing without a decision — the server already omitted
 * unfilled slots, so nothing shifts.
 *
 * Deliberate choices, each from the contract (docs/synced/portal-api/09-gotchas.md):
 * - `href` is `clickUrl` AS-IS. It counts the click and 302s onward; it works
 *   on middle-click and needs no JS. No click handler, no unwrapping. Opens
 *   in a new tab: the sponsor's site must not replace the portal page.
 * - Plain `<img>`, not next/image: the creative is a CDN URL outside our
 *   remote-pattern allowlist, sized by the box, and must not be re-encoded.
 *   `object-contain`, never cover: the admin promises advertisers that a
 *   banner is never cropped or stretched — a file of another proportion
 *   "sits in the box with a margin" — and the box is the SLOT's ratio (the
 *   decision carries no creative size), so contain is the only way to keep
 *   that promise. The margin is the frame's own surface.
 *   Lazy unless the caller says the slot is above the fold (`eager`) — an
 *   above-the-results leaderboard can be the page's largest paint.
 * - `impression` once per token, on mount; `viewable` once, after the MRC
 *   rule (lib/placements/viewability.ts: ≥ 50 % of the box, 30 % for a large
 *   one, against the smaller of box and viewport) has held for a continuous
 *   second. Never `viewable` on render — that turns the sponsor's best metric
 *   into the worst.
 * - The effect keys on the token, not the decision object: a parent
 *   re-render must not re-send. Repeats would be harmless anyway (a token
 *   counts once per kind), so strict-mode double-effects cannot inflate
 *   anything.
 * - Inside a `<DismissableAd>` nothing is sent until the wrapper has read the
 *   session's dismiss flag (`useDismissSettled`): a banner the visitor closed
 *   earlier in the session is server-rendered, then collapses — and must not
 *   be billed for the frame in between.
 * - Neutral names (`lh-placement`, this file, the directory): ad blockers
 *   filter on `ad-`, `banner`, `sponsor`, `promo` in class names and URLs;
 *   scripts/check-invariants.mjs (`neutral-naming`) keeps it that way.
 */
export function Placement({
  decision: d,
  eager = false,
}: {
  decision?: PlacementDecision | null;
  eager?: boolean;
}) {
  const ref = useRef<HTMLAnchorElement>(null);
  const t = useDict();
  const token = d?.token;
  const beaconUrl = d?.beaconUrl;
  const armed = useDismissSettled();

  useEffect(() => {
    const el = ref.current;
    if (!armed || !token || !beaconUrl || !el) return;
    const send = (k: "impression" | "viewable") => {
      const body = JSON.stringify({ t: token, k });
      // sendBeacon posts text/plain, which the endpoint reads as JSON. A
      // string body (not a JSON-typed Blob) is what every browser allows on
      // unload; fetch+keepalive is the fallback where sendBeacon is missing.
      // `no-cors` keeps it a simple request: no preflight, opaque response.
      if (typeof navigator.sendBeacon === "function" && navigator.sendBeacon(beaconUrl, body))
        return;
      void fetch(beaconUrl, { method: "POST", body, keepalive: true, mode: "no-cors" }).catch(
        () => {},
      );
    };
    send("impression");

    let timer: number | undefined;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (meetsViewabilityRule(entry, { width: window.innerWidth, height: window.innerHeight })) {
          timer ??= window.setTimeout(() => {
            send("viewable");
            io.disconnect();
          }, VIEWABLE_MS);
        } else {
          window.clearTimeout(timer);
          timer = undefined;
        }
      },
      { threshold: VIEWABILITY_THRESHOLDS },
    );
    io.observe(el);
    return () => {
      window.clearTimeout(timer);
      io.disconnect();
    };
  }, [armed, token, beaconUrl]);

  if (!d) return null;

  return (
    <PlacementFrame
      label={t.pages.adBadge}
      ratio={aspectRatioCss(d.aspectRatio, d.format)}
      format={d.format}
    >
      <a
        ref={ref}
        href={d.clickUrl}
        target="_blank"
        rel="noopener noreferrer nofollow sponsored"
        className="absolute inset-0 block"
      >
        {/* An unlabelled creative gets the legal "Ad" label: the decision carries no
            title, and this link has no other content to name it. */}
        {/* eslint-disable-next-line @next/next/no-img-element -- CDN creative outside the image allowlist; see the header comment */}
        <img
          src={d.imageUrl}
          alt={imageAlt(d.alt, t.pages.adBadge)}
          className="h-full w-full object-contain"
          decoding="async"
          loading={eager ? "eager" : "lazy"}
        />
      </a>
    </PlacementFrame>
  );
}
